/**
 * WMLogo — Logo global Wealth Management — Global Real Assets
 * (OS V3 · M1 · Piste P1 « Héritage » — bascule du 19/07/2026, réversible)
 *
 * Monogramme « WM » typographique institutionnel : capitales serif Cinzel
 * JUXTAPOSÉES (jamais entrelacées), or métallique sur fonds sombres, or
 * profond sur fonds clairs. Rendu SVG inline : vectoriel à toutes densités,
 * identique à l'écran et en print/PDF, zéro requête réseau.
 *
 * ROLLBACK DOCUMENTÉ (exigence PO) : l'ancien logo B1 est figé dans
 * ./WMLogo.v2.js (non importé) et ses assets PNG restent dans
 * /public/images/brand/. Retour V2 = recopier WMLogo.v2.js → WMLogo.js
 * (+ restaurer les favicons *.v2). Cf. memory/BRAND_BOOK_WMGRA_V3.md §6.3.
 *
 * Props (API STRICTEMENT INCHANGÉE — ~40 consommateurs) :
 *   variant="light"|"dark", mode="horizontal"|"markOnly", size="sm"|"md"|"lg"
 */
import React from 'react';

/* ─── Palette lettrage ────────────────────────────────────────────────── */
const GOLD_MID    = '#C5A572';   // gold textes/séparateur
const GOLD_DARK   = '#A68449';   // or profond (fonds clairs)
const NAVY        = '#0E1B33';
const IVORY       = '#F6F1E8';
const GRAPHITE    = '#3F4754';

const PALETTE = {
  light: {
    monogram:   GOLD_DARK,      // aplat or profond sur fonds clairs
    gradient:   false,
    primary:    NAVY,
    secondary:  GRAPHITE,
    separator:  GOLD_MID,
  },
  dark: {
    monogram:   null,           // dégradé métallique (défini dans le SVG)
    gradient:   true,
    primary:    GOLD_MID,
    secondary:  IVORY,
    separator:  GOLD_MID,
  },
};

/* ─── Tailles — ratio du monogramme V3 (2.2:1, plus horizontal que B1) ── */
const SIZES = {
  sm: {
    markW: 54,  markH: 25,
    titleCls: 'text-[13px] leading-[1.05]',
    subCls:   'text-[9px] leading-[1.1]',
    titleTracking: '0.08em',
    subTracking:   '0.14em',
    gap:      'gap-3',
    sepH:     'h-9',
  },
  md: {
    markW: 68,  markH: 31,
    titleCls: 'text-[18px] leading-[1.05]',
    subCls:   'text-[10px] leading-[1.1]',
    titleTracking: '0.10em',
    subTracking:   '0.18em',
    gap:      'gap-4',
    sepH:     'h-12',
  },
  lg: {
    markW: 112, markH: 51,
    titleCls: 'text-[26px] leading-[1.05]',
    subCls:   'text-[14px] leading-[1.1]',
    titleTracking: '0.10em',
    subTracking:   '0.20em',
    gap:      'gap-6',
    sepH:     'h-20',
  },
};

/* ─── Monogramme V3 « Héritage » — WM serif juxtaposé, SVG inline ─────── */
let uid = 0;
function MonogramWM({ width, height, monogram, gradient }) {
  // Id de gradient unique par instance (plusieurs logos par page possible).
  const gradId = React.useMemo(() => `wm-v3-gold-${++uid}`, []);
  return (
    <svg
      viewBox="0 0 220 100"
      width={width}
      height={height}
      overflow="visible"
      aria-hidden
      focusable="false"
      style={{
        display: 'block',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact',
      }}
    >
      {gradient && (
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#E8D5AE" />
            <stop offset="0.45" stopColor={GOLD_MID} />
            <stop offset="1" stopColor={GOLD_DARK} />
          </linearGradient>
        </defs>
      )}
      <text
        x="110"
        y="88"
        textAnchor="middle"
        fill={gradient ? `url(#${gradId})` : monogram}
        style={{
          fontFamily: "'Cinzel', 'Cormorant Garamond', serif",
          fontWeight: 600,
          fontSize: '90px',
          letterSpacing: '0.06em',
        }}
      >
        WM
      </text>
    </svg>
  );
}

/* ─── Composant principal ─────────────────────────────────────────────── */
export default function WMLogo({
  variant = 'light',
  mode = 'horizontal',
  size = 'md',
  className = '',
  ...rest
}) {
  const palette = PALETTE[variant] || PALETTE.light;
  const sizeCfg = SIZES[size] || SIZES.md;

  if (mode === 'markOnly') {
    return (
      <span
        className={`inline-flex items-center justify-center ${className}`}
        role="img"
        aria-label="Wealth Management — Global Real Assets"
        data-wmlogo="markOnly"
        data-variant={variant}
        {...rest}
      >
        <MonogramWM
          width={sizeCfg.markW}
          height={sizeCfg.markH}
          monogram={palette.monogram}
          gradient={palette.gradient}
        />
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center ${sizeCfg.gap} ${className}`}
      role="img"
      aria-label="Wealth Management — Global Real Assets"
      data-wmlogo="horizontal"
      data-variant={variant}
      {...rest}
    >
      {/* Monogramme V3 « Héritage » — WM serif juxtaposé. */}
      <MonogramWM
        width={sizeCfg.markW}
        height={sizeCfg.markH}
        monogram={palette.monogram}
        gradient={palette.gradient}
      />
      {/* Séparateur vertical doré fin. */}
      <span
        aria-hidden
        className={`block w-px ${sizeCfg.sepH}`}
        style={{ backgroundColor: palette.separator, opacity: 0.85 }}
      />
      {/* Texte : 2 lignes (uppercase serif Cinzel). */}
      <span className="flex flex-col">
        <span
          className={`uppercase ${sizeCfg.titleCls}`}
          style={{
            color: palette.primary,
            letterSpacing: sizeCfg.titleTracking,
            fontFamily: "'Cinzel', 'Bodoni Moda', 'Cormorant Garamond', serif",
            fontWeight: 600,
          }}
        >
          Wealth&nbsp;Management
        </span>
        <span
          className={`uppercase ${sizeCfg.subCls}`}
          style={{
            color: palette.secondary,
            letterSpacing: sizeCfg.subTracking,
            fontFamily: "'Cinzel', 'Bodoni Moda', 'Cormorant Garamond', serif",
            fontWeight: 500,
          }}
        >
          Global&nbsp;Real&nbsp;Assets
        </span>
      </span>
    </span>
  );
}
